import { type KeysToCamelCase, type Overwrite } from '@/types/app';

export enum SDStatus {
  draft = 'draft',
  inProgress = 'on_review',
  onRework = 'on_rework',
  completed = 'completed',
  cancelled = 'cancelled'
}

// SD - сокращение Service Desk
export type TSDResponse = {
  Support_Chats: TSDMessageResponse[];
  code: string | number;
  create_user: {
    email: string;
    first_name: string;
    last_name: string;
    middle_name: string | null;
    phone_number: string;
    uuid: string;
  };
  created_at: string;
  module: {
    code: string | number;
    name: string;
    uuid: string;
  };
  name: string;
  status: {
    code: SDStatus;
    name: string;
    uuid: string;
  };
  support_category: {
    code: string | number;
    name: string;
    uuid: string;
  };
  uuid: string;
};

export type TSDDetailedResponse = Overwrite<
  TSDResponse,
  {
    Support_Chats: TSDMessageDetailedResponse[];
  }
> & {
  Support_Files: TSDFile[];
  Support_Reasons_History: TSDStatusChangeResponse[];
  comment: string;
};

type TSDResponseCamelCased = KeysToCamelCase<TSDResponse>;
export type TSD = Overwrite<
  TSDResponseCamelCased,
  {
    code: string;
    module: Overwrite<
      TSDResponseCamelCased['module'],
      {
        code: string;
      }
    >;
    supportCategory: Overwrite<
      TSDResponseCamelCased['supportCategory'],
      {
        code: string;
      }
    >;
  }
>;

type TSDDetailedResponseCamelCased = KeysToCamelCase<TSDDetailedResponse>;
export type TSDDetailed = Overwrite<
  TSDDetailedResponseCamelCased,
  {
    code: string;
    module: Overwrite<
      TSDDetailedResponseCamelCased['module'],
      { code: string }
    >;
    supportCategory: Overwrite<
      TSDDetailedResponseCamelCased['supportCategory'],
      { code: string }
    >;
    supportChats: TSDMessageDetailed[];
    supportReasonsHistory: TSDStatusChange[];
  }
>;

export type TSDMessageCountResponse = {
  create_user: string;
  newMessageCount: number;
  newReasonHistoryCount: number;
  support_user: string;
  support_uuid: string;
};

export type TSDMessageCount = KeysToCamelCase<TSDMessageCountResponse>;

export type TSDChatItem = {
  createdAt: string;
  index: number;
  isRead: boolean;
  replyIndex: number;
  userType: 'user' | 'admin';
  uuid: string;
} & (
  | {
      type: 'message';
      item: TSDMessageDetailed;
    }
  | {
      type: 'status_change';
      item: TSDStatusChange;
    }
);

export type TSDReply = { chatItem: TSDChatItem; index: number } | null;

// --------------- Вспомогательные ---------------

// --------------- Message ---------------
export type TSDMessageResponse = {
  code: string | number;
  comment: string | null;
  created_at: string;
  user: {
    first_name: string | null;
    last_name: string | null;
    middle_name: string | null;
    uuid: string;
  };
  user_type: 'user' | 'admin';
  uuid: string;
};

export type TSDMessageDetailedResponse = TSDMessageResponse & {
  Support_Chats_Files: {
    files: {
      name: string;
      uuid: string;
    } | null;
  }[];
  is_read: boolean | null;
  reply_msg_uuid: string | null;
};

export type TSDMessage = Overwrite<
  KeysToCamelCase<TSDMessageResponse>,
  {
    code: string;
  }
>;

type TSDMessageDetailedResponseCamelCased =
  KeysToCamelCase<TSDMessageDetailedResponse>;
export type TSDMessageDetailed = Overwrite<
  TSDMessageDetailedResponseCamelCased,
  {
    code: string;
    supportChatsFiles: TSDMessageDetailedResponseCamelCased['supportChatsFiles'][number]['files'][];
  }
>;

// --------------- Files --------------
type TSDFile = {
  files: { name: string; uuid: string } | null;
  uuid: string;
};

// --------------- Status Change ---------------
type TSDStatusChangeResponse = {
  comment: string | null;
  created_at: string;
  is_read: boolean | null;
  status: {
    code: SDStatus;
    name: string;
    uuid: string;
  };
  support_reasons: {
    code: string;
    name: string;
    type: 'admin' | 'user';
    uuid: string;
  } | null;
  uuid: string;
};

export type TSDStatusChange = KeysToCamelCase<TSDStatusChangeResponse>;

// --------------- WebSocket ---------------
export type TSDChatUpdateWsResponse = [
  string,
  TSDMessageWsResponse,
  TSDUserWsResponse,
  SDStatus,
  TSDReasonWsResponse,
  TSDFilesWsResponse[]
];

export type TSDChatUpdateWs = {
  uuid: string;
  message: TSDMessageWs;
  user: TSDUserWs;
  status: SDStatus;
  reason: TSDReasonWs;
  files: TSDFilesWs[];
};

// --------------- Message ---------------
type TSDMessageWsResponse = Overwrite<
  Pick<
    TSDMessageDetailedResponse,
    'code' | 'comment' | 'created_at' | 'user_type' | 'reply_msg_uuid'
  >,
  {
    reply_msg_uuid?: string;
  }
> & {
  support_uuid: string;
  user_uuid: string;
};

type TSDMessageWs = KeysToCamelCase<TSDMessageWsResponse>;

// --------------- User ---------------
type TSDUserWsResponse = {
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  uuid: string;
};

type TSDUserWs = KeysToCamelCase<TSDUserWsResponse>;

// --------------- Reason ---------------
type TSDReasonWsResponse = {
  comment?: string;
  created_at: string;
  status_uuid: string;
  support_reasons_uuid?: string;
  support_uuid: string;
} | null;

type TSDReasonWs = KeysToCamelCase<TSDReasonWsResponse>;

// --------------- Files ---------------
type TSDFilesWsResponse = {
  fileId: {
    name: string;
    uuid: string;
  };
};

type TSDFilesWs = KeysToCamelCase<TSDFilesWsResponse['fileId']>;

// --------------- Attachments ---------------
export type TSDAttachment = {
  base64: string;
  name: string;
};

export type TSDNewAttachment = {
  base64: string;
  key: string;
  name: string;
};

export type TSDFilesMapItem = TSDAttachment & {
  state: 'initial' | 'loading' | 'loaded' | 'error';
};
